/**
 * Privacy Policy Page
 *
 * Describes how ContentSage collects, uses, and protects user data.
 */

import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import ContentSageLogo from '../components/ContentSageLogo';

const LAST_UPDATED = 'March 2, 2026';
const SUPPORT_EMAIL = 'privacy@contentsage.ai';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 bg-white/80 backdrop-blur-xl border-b border-gray-200 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <ContentSageLogo size={28} showText={true} />
          </Link>
          <Link
            to="/"
            className="flex items-center space-x-2 text-gray-500 hover:text-gray-900 transition-colors text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-8">Last updated: {LAST_UPDATED}</p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">1. Information We Collect</h2>
            <p>We collect the following types of information:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Account Information:</strong> Name, email address, and password when you register</li>
              <li><strong>Payment Information:</strong> Processed securely by Stripe. We do not store credit card numbers</li>
              <li><strong>Usage Data:</strong> Content generation history, feature usage, and analytics events</li>
              <li><strong>Platform Credentials:</strong> OAuth tokens for connected platforms (WordPress, Ghost, Wix), encrypted at rest</li>
              <li><strong>Device Information:</strong> Browser type, operating system, and device identifiers for mobile apps</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">2. How We Use Your Information</h2>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Provide and improve the Service</li>
              <li>Process payments and manage subscriptions</li>
              <li>Publish content to your connected platforms on your behalf</li>
              <li>Send transactional emails (account verification, password resets, billing)</li>
              <li>Analyze usage patterns to improve features</li>
              <li>Detect and prevent fraud or abuse</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">3. Data Storage and Security</h2>
            <p>
              Your data is stored on secure servers hosted by Railway (infrastructure provider).
              Platform credentials (OAuth tokens) are encrypted at rest using AES-128-CBC encryption
              with PBKDF2 key derivation. We use HTTPS for all data in transit.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">4. Third-Party Services</h2>
            <p>We use the following third-party services that may process your data:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Stripe:</strong> Payment processing</li>
              <li><strong>Firebase:</strong> Authentication</li>
              <li><strong>Google Cloud:</strong> AI content generation (Gemini)</li>
              <li><strong>Anthropic:</strong> AI content generation (Claude)</li>
              <li><strong>Sentry:</strong> Error monitoring</li>
              <li><strong>Google Analytics:</strong> Website analytics (if configured)</li>
            </ul>
            <p className="mt-2">
              Each service has its own privacy policy governing how they handle data.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">5. Data Retention</h2>
            <p>
              We retain your account data for as long as your account is active. Generated content
              history is retained for 90 days. Analytics data is aggregated and anonymized after 30 days.
              Upon account deletion, personal data is removed within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Delete your account and associated data</li>
              <li>Export your data in a portable format</li>
              <li>Opt out of marketing communications</li>
              <li>Disconnect third-party platform integrations at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">7. Cookies</h2>
            <p>
              We use essential cookies for authentication and session management. We do not use
              third-party tracking cookies. Analytics, when enabled, use first-party data collection.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">8. Children's Privacy</h2>
            <p>
              The Service is not intended for children under 13. We do not knowingly collect personal
              information from children.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify users of material
              changes via email or in-app notification. Continued use after changes constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-gray-900 mt-8 mb-3">10. Contact</h2>
            <p>
              For privacy-related questions or to exercise your rights, contact us at{' '}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-indigo-600 hover:text-indigo-700 underline">
                {SUPPORT_EMAIL}
              </a>.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}
